import * as React from 'react';
import { cn } from '../lib/cn';

export interface OtpInputProps {
  /** Number of OTP digits. Default: 6 */
  length?: number;
  /** Controlled string value (e.g. "123456" or "12" for partial) */
  value: string;
  onChange: (value: string) => void;
  /** Called when all digits are filled */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** Show error styling on all boxes */
  error?: boolean;
  className?: string;
  /** aria-label for the group */
  label?: string;
  /** Focus the first box on mount (e.g. when the OTP step first appears). */
  autoFocus?: boolean;
}

/**
 * Segmented OTP input — renders N individual digit boxes.
 *
 * Behaviours:
 * - Typing a digit advances focus to the next box.
 * - Backspace clears the current digit (or moves focus left if already empty).
 * - Arrow keys move focus left/right.
 * - Pasting a numeric string distributes digits across boxes.
 * - `autoComplete="one-time-code"` on the first box enables iOS SMS autofill;
 *   an autofilled full code (delivered into one box at once) is spread across
 *   every box by `handleChange`. Android WebOTP fills via the controlled value.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  className,
  label = 'One-time password',
  autoFocus = false,
}: OtpInputProps) {
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Derive per-box digit array from the controlled string value
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  // Focus the first box when the component mounts so the user can type (and
  // receive the SMS suggestion) without an extra tap. Mount-only — it never
  // steals focus while the user is already editing.
  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function focusAt(idx: number) {
    refs.current[idx]?.focus();
  }

  function update(nextDigits: string[]) {
    const next = nextDigits.join('');
    onChange(next);
    if (next.length === length) {
      onComplete?.(next);
    }
  }

  function handleChange(idx: number, e: React.ChangeEvent<HTMLInputElement>) {
    // Strip non-numeric. OS autofill (iOS "one-time-code" suggestion, Android
    // WebOTP) and fast typing can drop MULTIPLE digits into one box at once, so
    // distribute everything we received across the boxes starting at `idx`
    // rather than keeping a single character.
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      // Input cleared (e.g. via mobile autocomplete then backspace)
      const next = [...digits];
      next[idx] = '';
      update(next);
      return;
    }

    const next = [...digits];
    for (let i = 0; i < raw.length && idx + i < length; i += 1) {
      next[idx + i] = raw[i]!;
    }
    update(next);

    // Advance focus to just past the last box we filled (clamped to the end).
    const lastFilled = Math.min(idx + raw.length, length) - 1;
    focusAt(Math.min(lastFilled + 1, length - 1));
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = '';
        update(next);
      } else if (idx > 0) {
        const next = [...digits];
        next[idx - 1] = '';
        update(next);
        focusAt(idx - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (idx > 0) focusAt(idx - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (idx < length - 1) focusAt(idx + 1);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const next = [...digits];
      next[idx] = '';
      update(next);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;

    const next = Array.from({ length }, (_, i) => pasted[i] ?? digits[i] ?? '');
    update(next);

    // Focus last filled box (or last box if fully filled)
    const nextFocus = Math.min(pasted.length, length - 1);
    focusAt(nextFocus);
  }

  return (
    <div
      role="group"
      aria-label={label}
      className={cn('flex gap-2 sm:gap-3', className)}
    >
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={digit}
          disabled={disabled}
          aria-label={`Digit ${idx + 1} of ${length}`}
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            'w-10 h-12 sm:w-11 sm:h-13',
            'text-center text-lg font-semibold',
            'rounded-lg border bg-background transition-all duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-destructive text-destructive focus-visible:ring-destructive/30'
              : digit
                ? 'border-primary/60 bg-primary/5 text-foreground'
                : 'border-input text-foreground',
          )}
        />
      ))}
    </div>
  );
}
